import React, { useState, useEffect } from 'react';
import { format, setMonth, setYear, getYear, getMonth } from 'date-fns';
import { ArrowLeft, ArrowRight, Calendar as CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useBusinessCreationValidation, BusinessCreationInput } from '@/lib/business';
import { cn } from '@/lib/core/utils';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export interface MonthPickerProps {
  /** Currently selected date */
  value: Date;
  /** Callback when month changes */
  onChange: (date: Date) => void;
  /** Business creation date for validation (disables months before this) */
  businessCreationDate?: BusinessCreationInput;
  /** Button size variant */
  size?: 'sm' | 'default';
  /** Whether the picker is disabled */
  disabled?: boolean;
  /** Additional className for the trigger button */
  className?: string;
  /** Show "Go to Today" button in the dropdown */
  showTodayButton?: boolean;
  /** Custom handler for "Go to Today" button (if not provided, uses onChange with new Date()) */
  onTodayClick?: () => void;
  /** Popover alignment */
  align?: 'start' | 'center' | 'end';
  /** Data attribute for tutorials */
  dataTutorial?: string;
}

export const MonthPicker: React.FC<MonthPickerProps> = ({
  value,
  onChange,
  businessCreationDate,
  size = 'default',
  disabled = false,
  className,
  showTodayButton = true,
  onTodayClick,
  align = 'center',
  dataTutorial,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState(getYear(value));

  const currentYear = getYear(value);
  const currentMonth = getMonth(value);

  // Use centralized month availability service
  const {
    isBeforeBusinessCreation,
    isYearNavigationDisabled,
  } = useBusinessCreationValidation(businessCreationDate);

  // Sync year when value changes
  useEffect(() => {
    setSelectedYear(getYear(value));
  }, [value]);

  const handleMonthSelect = (monthIndex: number) => {
    let newDate = setYear(value, selectedYear);
    newDate = setMonth(newDate, monthIndex);
    onChange(newDate);
    setIsOpen(false);
  };

  const handleTodayClick = () => {
    if (onTodayClick) {
      onTodayClick();
    } else {
      const today = new Date();
      onChange(today);
      setSelectedYear(getYear(today));
    }
    setIsOpen(false);
  };

  // Fixed width classes based on size
  const widthClass = 'min-w-[170px]';

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size={size === 'sm' ? 'sm' : 'default'}
          disabled={disabled}
          className={cn(widthClass, className)}
          data-tutorial={dataTutorial}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {format(value, 'MMMM yyyy')}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-60 p-4" align={align}>
        <div className="space-y-4">
          {/* Year Navigation */}
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="icon"
              onClick={() => {
                const newYear = selectedYear - 1;
                if (!isYearNavigationDisabled(newYear, 'previous')) {
                  setSelectedYear(newYear);
                }
              }}
              disabled={isYearNavigationDisabled(selectedYear - 1, 'previous')}
            >
              <ArrowLeft className="h-5 w-5" />
              <span className="sr-only">Previous Year</span>
            </Button>

            <span className="text-lg font-semibold">{selectedYear}</span>

            <Button
              variant="outline"
              size="icon"
              onClick={() => setSelectedYear(selectedYear + 1)}
            >
              <ArrowRight className="h-5 w-5" />
              <span className="sr-only">Next Year</span>
            </Button>
          </div>

          {/* Month Grid */}
          <div className="grid grid-cols-3 gap-2">
            {MONTHS.map((month, index) => {
              const isDisabled = isBeforeBusinessCreation(index, selectedYear);
              const isSelected = index === currentMonth && selectedYear === currentYear;

              return (
                <Button
                  key={index}
                  variant={isSelected ? 'default' : 'outline'}
                  size="sm"
                  className="h-8"
                  disabled={isDisabled}
                  onClick={() => handleMonthSelect(index)}
                >
                  {month.slice(0, 3)}
                </Button>
              );
            })}
          </div>

          {/* Today Button */}
          {showTodayButton && (
            <Button
              variant="outline"
              className="w-full"
              onClick={handleTodayClick}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              Go to Today
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default MonthPicker;
